import { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import IUMRSNavBar from "./IUMRSNavbar";
import { ICAMNavroute } from "../../../utils/ICAMnavroute.js";
import IUMRSFooter from "./IUMRSFooter";
import { useDesktopSiteDetection } from "../../../hooks/mobile_desktop_mode.jsx";
import {IUMRSGalleryPhotos as PHOTOS} from "../../../utils/IUMRSGalleryPhotos.js";


const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
    `;

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
    `

const theme = {
    navy:     "#0d1e3d",
    navyMid:  "#16305e",
    gold:     "#c9944a",
    goldLt:   "#e8b96a",
    white:    "#ffffff",
    offWhite: "#f5f7fb",
    gray:     "#8a97b0",
    grayLt:   "#dde3ef",
    };



const Lightbox = ({ photos, currentIdx, onClose, onNavigate }) => {
    const photo = photos[currentIdx];
    const touchStartX = useRef(null);

  useEffect(() => {
    document.body.classList.add("lightbox-open");
    return () => document.body.classList.remove("lightbox-open");
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape")      onClose();
      if (e.key === "ArrowLeft")   onNavigate(-1);
      if (e.key === "ArrowRight")  onNavigate(1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNavigate]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) onNavigate(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  if (!photo) return null;
  return (
            <LightboxBackdrop $open onClick={(e) => e.target === e.currentTarget && onClose()}
                onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                <LbClose onClick={onClose} aria-label="Close">
                    <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </LbClose>
                
                <LbNav $prev onClick={() => onNavigate(-1)} aria-label="Previous">
                    <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6" /></svg>
                </LbNav>
                <LbNav onClick={() => onNavigate(1)} aria-label="Next">
                    <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
                </LbNav>
                <LightboxInner>
                    <LightboxImgWrap>
                    <img src={photo.src.replace("w=800", "w=1400")} alt={photo.title} />
                    </LightboxImgWrap>
                    <LightboxCaption>
                    <strong>{photo.title}</strong>
                    <span>{photo.sub}</span>
                    </LightboxCaption>
                </LightboxInner>
                <LbCounter>{currentIdx + 1} / {photos.length}</LbCounter>
            </LightboxBackdrop>
  )
}



const IUMRSGallery = () => {

    const FILTERS = [
    { key: "all",        label: "All Photos" },
    { key: "opening",    label: "Opening Ceremony" },
    { key: "plenary",    label: "Plenary Sessions" },
    { key: "session",    label: "Symposia" },
    { key: "networking", label: "Networking & Posters" },
    { key: "banquet",    label: "Banquet" },
    { key: "excursion",  label: "Excursion" },
    ];
    
    const ExpandIcon = () => (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
        </svg>
    );

    const isDesktopSiteOnMobile = useDesktopSiteDetection()
    
    const [activeFilter, setActiveFilter] = useState("all");
    const [lightbox, setLightbox]         = useState({ open: false, idx: 0 });

    const filtered = activeFilter === "all"
    ? PHOTOS
    : PHOTOS.filter(p => p.cat === activeFilter);

    const openLightbox  = (idx) => setLightbox({ open: true, idx });
    const closeLightbox = useCallback(() => setLightbox(lb => ({ ...lb, open: false })), []);
    const navigate      = useCallback((dir) => {
    setLightbox(lb => ({
        ...lb,
        idx: (lb.idx + dir + filtered.length) % filtered.length,
    }));
    }, [filtered.length]);

    const handleFilterChange = (key) => {
    setActiveFilter(key);
    if (lightbox.open) closeLightbox();
    };

    

    

    return (
        <Wrapper>
            <IUMRSNavBar navroute={ICAMNavroute} />
            
            <Body className={isDesktopSiteOnMobile ? 'desktop-site-mode' : ''}>

                <MaxWidth>
                    <Header>
                    <Title>
                        Photo <TitleHighlight>Gallery</TitleHighlight>
                    </Title>
                    <Subtitle>19th Edition of International Conference on Advanced Materials (ICAM), Ghana</Subtitle>
                    </Header>
                </MaxWidth>


            {/* FILTERS */}
            <FiltersWrap>
                {FILTERS.map(f => (
                <FilterBtn
                    key={f.key}
                    $active={activeFilter === f.key}
                    onClick={() => handleFilterChange(f.key)}
                >
                    {f.label}
                </FilterBtn>
                ))}
            </FiltersWrap>

            {/* GALLERY */}
            <GallerySection>
                <CountLabel>
                {activeFilter === "all"
                    ? `Showing all ${filtered.length} photos`
                    : `${filtered.length} photo${filtered.length !== 1 ? "s" : ""} in this category`}
                </CountLabel>

                {filtered.length === 0 ? (
                <EmptyState>
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <path d="M3 9l4-4 4 4 4-5 4 5" />
                    </svg>
                    <p>No photos in this category yet.</p>
                </EmptyState>
                ) : (
                <MasonryGrid>
                    {filtered.map((photo, idx) => (
                    <PhotoCard
                        key={photo.id}
                        $delay={idx * 50}
                        onClick={() => openLightbox(idx)}
                        role="button"
                        tabIndex={0}
                        aria-label={`View photo: ${photo.title}`}
                        onKeyDown={(e) => e.key === "Enter" && openLightbox(idx)}
                    >
                        <img src={photo.src} alt={photo.title} loading="lazy" />
                        <PhotoOverlay>
                        <OverlayCaption>
                            {photo.title}
                            <span>{photo.sub}</span>
                        </OverlayCaption>
                        <OverlayExpand>
                            <ExpandIcon />
                        </OverlayExpand>
                        </PhotoOverlay>
                    </PhotoCard>
                    ))}
                </MasonryGrid>
                )}
            </GallerySection>

            {/* LIGHTBOX */}
            {lightbox.open && (
                <Lightbox
                photos={filtered}
                currentIdx={lightbox.idx}
                onClose={closeLightbox}
                onNavigate={navigate}
                />
            )}
            </Body>

            <IUMRSFooter />
        </Wrapper>
    )
}

const Wrapper = styled.div`
 min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%);
`

const Body = styled.div`

`;

const MaxWidth = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  color: #082D4A;
  margin-bottom: 16px;
  letter-spacing: -1px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const TitleHighlight = styled.span`
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 8px;
    left: 0;
    right: 0;
    height: 12px;
    background: rgba(8, 45, 74, 0.15);
    z-index: -1;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #4a5f7a;
  font-weight: 500;
`;


/* FILTERS */
const FiltersWrap = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-shrink: 0;
  position: sticky; //Needs work
  flex-wrap: wrap;
  padding: 32px 24px 20px;
  z-index: 999
`;

const FilterBtn = styled.button`
  padding: 8px 22px;
  border-radius: 100px;
  border: 1.5px solid ${p => p.$active ? theme.navy : theme.grayLt};
  background: ${p => p.$active ? theme.navy : theme.white};
  color: ${p => p.$active ? theme.white : theme.navy};
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: ${p => p.$active ? theme.navy : theme.gold};
    color: ${p => p.$active ? theme.white : theme.gold};
  }
`;

const GallerySection = styled.section`
  padding: 12px 32px 64px;
  max-width: 1400px;
  margin: 0 auto;
`;

const CountLabel = styled.p`
  font-size: 0.82rem;
  color: ${theme.gray};
  text-align: right;
  margin-bottom: 16px;
  font-weight: 500;
`;

const MasonryGrid = styled.div`
  columns: 4;
  column-gap: 14px;
  @media (max-width: 1100px) { columns: 3; }
  @media (max-width: 700px)  { columns: 2; }
  @media (max-width: 430px)  { columns: 1; }
`;

const cardEntrance = css`
  animation: ${fadeUp} 0.45s ease both;
`;

const PhotoCard = styled.div`
  break-inside: avoid;
  margin-bottom: 14px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  background: ${theme.grayLt};
  ${cardEntrance}
  animation-delay: ${p => p.$delay}ms;
  transition: box-shadow 0.25s, transform 0.25s;
  &:hover {
    box-shadow: 0 12px 36px rgba(13,30,61,0.18);
    transform: translateY(-3px) scale(1.01);
  }
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.4s ease;
  }
  &:hover img { transform: scale(1.04); }
`;

const PhotoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(13,30,61,0.75) 0%, transparent 55%);
  opacity: 0;
  transition: opacity 0.3s;
  display: flex;
  align-items: flex-end;
  padding: 16px;
  ${PhotoCard}:hover & { opacity: 1; }
`;

const OverlayCaption = styled.div`
  color: ${theme.white};
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.35;
  span {
    display: block;
    color: ${theme.goldLt};
    font-size: 0.72rem;
    font-weight: 400;
    margin-top: 2px;
  }
`;

const OverlayExpand = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 34px;
  height: 34px;
  background: rgba(255,255,255,0.18);
  backdrop-filter: blur(4px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
  ${PhotoCard}:hover & { opacity: 1; }
  svg { width: 16px; height: 16px; stroke: white; fill: none; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 24px;
  svg { width: 48px; height: 48px; stroke: ${theme.grayLt}; display: block; margin: 0 auto 16px; }
  p { color: ${theme.gray}; font-size: 0.95rem; }
`;

/* LIGHTBOX */
const LightboxBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(8,16,32,0.96);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => p.$open ? 1 : 0};
  pointer-events: ${p => p.$open ? "all" : "none"};
  transition: opacity 0.3s;
  backdrop-filter: blur(6px);
  animation: ${p => p.$open ? css`${fadeIn} 0.3s ease` : "none"};
`;

const LightboxInner = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LightboxImgWrap = styled.div`
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0,0,0,0.5);
  img {
    display: block;
    max-width: 88vw;
    max-height: 76vh;
    object-fit: contain;
  }
`;

const LightboxCaption = styled.div`
  margin-top: 16px;
  text-align: center;
  strong { display: block; color: ${theme.white}; font-size: 1rem; font-weight: 600; }
  span   { color: ${theme.goldLt}; font-size: 0.85rem; }
`;

const LbClose = styled.button`
  position: fixed;
  top: 24px;
  right: 28px;
  background: rgba(255,255,255,0.12);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  color: white;
  &:hover { background: rgba(255,255,255,0.22); }
  svg { width: 20px; height: 20px; stroke: white; fill: none; stroke-width: 2; }
`;



const LbNav = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${p => p.$prev ? "left: 20px;" : "right: 20px;"}
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.20); }
  svg { width: 22px; height: 22px; stroke: white; fill: none; stroke-width: 2; }
`;

const LbCounter = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  color: ${theme.gray};
  font-size: 0.8rem;
  letter-spacing: 0.08em;
`;

export default IUMRSGallery